import { ref, watch, onBeforeUnmount, nextTick, type Ref } from 'vue'

export type MenuDirection = 'up' | 'down'

export type MenuStyle = {
  position: 'fixed'
  top?: string
  bottom?: string
  left: string
  width: string
  zIndex: string
}

export type UseMenuPositioningOptions = {
  anchorRef: Ref<HTMLElement | null>
  isOpen: Ref<boolean>
  minBottomSpace?: number
  offset?: number
  zIndex?: number
}

export function useMenuPositioning(options: UseMenuPositioningOptions) {
  const { anchorRef, isOpen, minBottomSpace = 100, offset = 5, zIndex = 9999 } = options

  const menuDirection = ref<MenuDirection>('down')
  const menuStyle = ref<MenuStyle>({
    position: 'fixed',
    top: '0px',
    bottom: 'auto',
    left: '0px',
    width: 'auto',
    zIndex: String(zIndex),
  })

  let observer: IntersectionObserver | null = null

  const updateMenuPosition = () => {
    const anchor = anchorRef.value
    if (!anchor) return

    const rect = anchor.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const spaceAbove = rect.top

    // Déterminer la direction
    const shouldOpenUp = spaceBelow < minBottomSpace && spaceAbove > spaceBelow
    menuDirection.value = shouldOpenUp ? 'up' : 'down'

    // Calculer le style
    if (shouldOpenUp) {
      menuStyle.value = {
        position: 'fixed',
        top: 'auto',
        bottom: `${window.innerHeight - rect.top + offset}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        zIndex: String(zIndex),
      }
    } else {
      menuStyle.value = {
        position: 'fixed',
        top: `${rect.bottom + offset}px`,
        bottom: 'auto',
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        zIndex: String(zIndex),
      }
    }
  }

  const onScroll = () => {
    if (isOpen.value) {
      updateMenuPosition()
    }
  }

  const setupObserver = () => {
    const anchor = anchorRef.value
    if (!anchor) return

    observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (!entry?.isIntersecting && isOpen.value) {
          isOpen.value = false
        }
      },
      { threshold: 0.1 },
    )
    observer.observe(anchor)
  }

  const cleanupObserver = () => {
    if (observer) {
      observer.disconnect()
      observer = null
    }
  }

  watch(isOpen, (open) => {
    if (open) {
      nextTick(() => {
        updateMenuPosition()
        setupObserver()
      })
      window.addEventListener('scroll', onScroll, true)
      window.addEventListener('resize', updateMenuPosition)
    } else {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', updateMenuPosition)
      cleanupObserver()
    }
  })

  onBeforeUnmount(() => {
    window.removeEventListener('scroll', onScroll, true)
    window.removeEventListener('resize', updateMenuPosition)
    cleanupObserver()
  })

  return {
    menuDirection,
    menuStyle,
    updateMenuPosition,
  }
}
